import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import { BaseDynamicLayoutHeaderComponent } from '@common-modules/dynamic-layout/base-dynamic-layout-header.component';
import { DynamicLayoutItemSettings } from '@common-modules/dynamic-layout/models/dynamic-layout-item-settings';
import { SetValueAction } from '@common-modules/dynamic-layout/state/generic/generic.actions';
import { StateAreas } from '@common-modules/redux/models/state-areas';
import { StateFullSettings } from '@common-modules/redux/models/state-full-settings';
import { StateWidgetSettings } from '@common-modules/redux/models/state-widget-settings';
import { ReduxStateService } from '@common-modules/redux/redux-state.service';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { WtrColors } from '@common-modules/shared/styles/wtr-colors';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  MasterOperatorConnectPopup,
  MasterOperatorConnectPopupConfiguration,
} from '../master-operator-connect-popup/master-operator-connect-popup.component';
import {
  MasterOperatorRealtimeConnectPopup,
  MasterOperatorRealtimeConnectPopupConfiguration,
} from '../master-operator-realtime-connect-popup/master-operator-realtime-connect-popup.component';
import { MasterOperatorSingleSettings } from '../master-operator-single/master-operator-single-settings';
import { MO_CONNECT_POPUP_SIZE } from '../master-operator.constants';
import { IConnectIndicatorSettings } from '../models/connect-indicator-settings';
import { MasterOperatorConnectItemsSettings } from '../models/master-operator-connect-settings';
import { MasterOperatorFields } from '../models/master-operator-fields.enum';
import { MasterOperatorType } from '../models/master-operator-type';

const COMPONENT_SELECTOR = 'tc-master-operator-single-toolbar';
@UntilDestroy()
@Component({
    selector: COMPONENT_SELECTOR,
    templateUrl: './master-operator-single-toolbar.component.html',
    styleUrls: ['./master-operator-single-toolbar.component.scss'],
    providers: [ReduxStateService],
    standalone: false
})
export class MasterOperatorSingleToolbarComponent
  extends BaseDynamicLayoutHeaderComponent
  implements OnInit, OnDestroy
{
  T_SCOPE = `${TranslateScopes.MasterOperator}.${COMPONENT_SELECTOR}`;
  readonly color = WtrColors.PrimaryColor;
  MasterOperatorType = MasterOperatorType;
  getItemSetting: () => DynamicLayoutItemSettings;
  masterOperatorSettings: MasterOperatorSingleSettings;
  connectIndicatorSettings: IConnectIndicatorSettings;
  private _stateWidgetSettings;

  constructor(
    private readonly _operatorPopup: MatDialog,
    private readonly _state: ReduxStateService
  ) {
    super();
  }

  ngOnInit(): void {
    const settings = this.getItemSetting();
    this.masterOperatorSettings = settings.params;

    this._stateWidgetSettings = new StateWidgetSettings({
      module: this.masterOperatorSettings.layout.layoutArea,
      page: this.masterOperatorSettings.layout.layoutKey,
      scopeInstanceKeys: this.masterOperatorSettings.layout.scopeInstanceKeys,
    });

    this.updateConnectIndicator(this.masterOperatorSettings.connect);

    this._state.configure(this._stateWidgetSettings);
  }

  openNewOperatorPopup() {
    this.masterOperatorSettings = this.getItemSetting().params;

    const dialogConfig = this.buildDialogConfig();
    const popupSettings = new MasterOperatorConnectPopupConfiguration({
      settings: this.masterOperatorSettings,
      realtimeIds: this.masterOperatorSettings.connect.items.map((x) => x.instanceId),
    });
    dialogConfig.data = popupSettings;
    const popup = this._operatorPopup.open(MasterOperatorConnectPopup, dialogConfig);

    popup
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((settings) => {
        if (!settings) {
          return;
        }

        const connectSettings = globalUtilsHelper.clone(settings, true);

        const action = new SetValueAction({
          fieldName: MasterOperatorFields.OperatorConnectSettings,
          value: connectSettings,
        });

        const nullAction = new SetValueAction({
          fieldName: MasterOperatorFields.OperatorConnectSettings,
          value: null,
        });

        const fullSettings = new StateFullSettings({
          widget: new StateWidgetSettings({
            module: 'MasterOperator',
            scopeInstanceKeys: {
              [StateAreas.Generic]: 'shared',
            },
            page: 'Main',
          }),
          area: action.area,
          scope: action.settings.scope,
        });

        this._state.dispatchConfigured(nullAction, fullSettings);
        this._state.dispatchConfigured(action, fullSettings);
      });
  }

  openOperatorPopup() {
    this.masterOperatorSettings = this.getItemSetting().params;

    const dialogConfig = this.buildDialogConfig();
    const popupSettings = new MasterOperatorConnectPopupConfiguration({
      settings: this.masterOperatorSettings,
      realtimeIds: this.masterOperatorSettings.connect.items.map((x) => x.realTimeId),
      modifyExistingOperators: true,
    });
    dialogConfig.data = popupSettings;
    const popup = this._operatorPopup.open(MasterOperatorConnectPopup, dialogConfig);

    popup
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((settings) => {
        if (!settings) {
          return;
        }

        const connectSettings: MasterOperatorConnectItemsSettings = globalUtilsHelper.clone(
          settings.connect,
          true
        );

        this.dispatchConnectSettings(null);
        this.dispatchConnectSettings(connectSettings);
        this.updateConnectIndicator(connectSettings);
      });
  }

  openRealtimePopup() {
    this.masterOperatorSettings = this.getItemSetting().params;

    const dialogConfig = this.buildDialogConfig();
    const popupSettings = new MasterOperatorRealtimeConnectPopupConfiguration({
      settings: this.masterOperatorSettings,
    });
    dialogConfig.data = popupSettings;
    const popup = this._operatorPopup.open(MasterOperatorRealtimeConnectPopup, dialogConfig);

    popup
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((settings) => {
        if (!settings) {
          return;
        }

        const connectSettings: MasterOperatorConnectItemsSettings = globalUtilsHelper.clone(
          settings.connect,
          true
        );

        this.dispatchConnectSettings(null);
        this.dispatchConnectSettings(connectSettings);
        this.updateConnectIndicator(connectSettings);
      });
  }

  private dispatchConnectSettings(connectSettings: MasterOperatorConnectItemsSettings): void {
    this._state.dispatch(
      new SetValueAction({
        fieldName: MasterOperatorFields.ConnectSettings,
        value: connectSettings,
      })
    );
  }

  private buildDialogConfig(): MatDialogConfig {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = MO_CONNECT_POPUP_SIZE;
    return dialogConfig;
  }

  private updateConnectIndicator(connectSettings: MasterOperatorConnectItemsSettings): void {
    const connectItems = connectSettings.items;
    if (connectItems.length) {
      this.connectIndicatorSettings = connectItems[0];
    }
  }

  ngOnDestroy(): void {
    this._state.dispatch(
      new SetValueAction({
        fieldName: MasterOperatorFields.OperatorConnectSettings,
        value: null,
      })
    );

    this.dispatchConnectSettings(null);
  }
}
